import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import type { All8PageContent, ImageFieldValue } from "@bcc/core-renderers";
import HrefInput from "../HrefInput";
import ImagePickerInput from "../ImagePickerInput";
import FilePickerInput from "../FilePickerInput";
import CategoryPathSelect from "../CategoryPathSelect";
import type {
	ArrayFieldConfig,
	ContentFieldSelectFieldConfig,
	ContentFieldValueFieldConfig,
	DynamicPageSelectFieldConfig,
	FieldConfig,
	Path
} from "../../Pages/pageComponentLibrary";
import type { HrefTarget } from "../../types/layout";
import { getValueAtPath } from "./pageEditorUtils";
import { ParagraphArrayField } from "./ParagraphArrayField";
import { RichTextEditorField } from "./RichTextEditorField";
import type { IApplicationState } from "../../Redux/Reducers";
import PageEditorObjectField from "./PageEditorObjectField";
import PageEditorArrayField from "./PageEditorArrayField";
import ContentFieldSelectControl from "./ContentFieldSelectControl";
import ContentFieldValueControl from "./ContentFieldValueControl";
import DynamicPageSelectControl from "./DynamicPageSelectControl";

type PageEditorFieldControlProps = {
	editable: boolean;
	field: FieldConfig;
	componentIndex: number;
	parentPath?: Path;
	previewComponents: All8PageContent;
	updateComponentValue: (index: number, path: Path, value: any) => void;
	allowImageVariables?: boolean;
};

/** Generic page editor field control supporting text, image, link, table and nested objects. */
const PageEditorFieldControl: React.FC<PageEditorFieldControlProps> = ({
	editable,
	field,
	componentIndex,
	parentPath = [],
	previewComponents,
	updateComponentValue,
	allowImageVariables
}) => {
	const categories = useSelector((state: IApplicationState) => state.categories.items);
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);
	const fullPath = [...parentPath, ...field.path];
	const value = getValueAtPath(previewComponents[componentIndex], fullPath);
	const isImageField = field.type === "image";
	const imageValue = (value as ImageFieldValue | null | undefined) ?? "";
	const imageSrc = typeof imageValue === "string" ? imageValue : imageValue?.src ?? "";
	const imageOptions = typeof imageValue === "object" && imageValue ? imageValue.options : undefined;
	const isVariableValue =
		isImageField && typeof imageSrc === "string" && imageSrc.trim().startsWith("{") && imageSrc.trim().endsWith("}");
	const [imageVariableMode, setImageVariableMode] = useState<boolean>(isVariableValue);

	const showWhen = (field as any).showWhen as { path: Path; equals?: any; notEquals?: any } | undefined;
	const shouldRender = (() => {
		if (!showWhen) return true;
		const target = getValueAtPath(previewComponents[componentIndex], showWhen.path);
		if ("equals" in showWhen) return target === showWhen.equals;
		if ("notEquals" in showWhen) return target !== showWhen.notEquals;
		return true;
	})();

	useEffect(() => {
		if (isImageField) {
			setImageVariableMode(isVariableValue);
		}
	}, [isImageField, isVariableValue]);

	if (!shouldRender) return null;

	if (field.type === "text" || field.type === "url") {
		return (
			<div className="space-y-1">
				<label className="text-xs font-semibold text-slate-600">{field.label}</label>
				<input
					type={field.type === "url" ? "url" : "text"}
					value={(value ?? "") as string}
					placeholder={field.placeholder}
					onChange={(e) => updateComponentValue(componentIndex, fullPath, e.target.value)}
					disabled={!editable}
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
				/>
				{field.helper && <p className="text-[11px] text-slate-500">{field.helper}</p>}
			</div>
		);
	}

	if (field.type === "number") {
		return (
			<div className="space-y-1">
				<label className="text-xs font-semibold text-slate-600">{field.label}</label>
				<input
					type="number"
					value={value ?? ""}
					placeholder={field.placeholder}
					onChange={(e) => {
						const raw = e.target.value;
						const nextValue = raw === "" ? "" : Number(raw);
						updateComponentValue(componentIndex, fullPath, Number.isNaN(nextValue) ? "" : nextValue);
					}}
					disabled={!editable}
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
				/>
				{field.helper && <p className="text-[11px] text-slate-500">{field.helper}</p>}
			</div>
		);
	}

	if (field.type === "richtext") {
		return (
			<div className="space-y-1">
				<label className="text-xs font-semibold text-slate-600">{field.label}</label>
				<RichTextEditorField
					value={(value ?? "") as string}
					onChange={(html) => updateComponentValue(componentIndex, fullPath, html)}
					editable={editable}
				/>
				{field.helper && <p className="text-[11px] text-slate-500">{field.helper}</p>}
			</div>
		);
	}

	if (field.type === "textarea") {
		return (
			<div className="space-y-1">
				<label className="text-xs font-semibold text-slate-600">{field.label}</label>
				<textarea
					value={(value ?? "") as string}
					placeholder={field.placeholder}
					onChange={(e) => updateComponentValue(componentIndex, fullPath, e.target.value)}
					rows={3}
					disabled={!editable}
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
				/>
				{field.helper && <p className="text-[11px] text-slate-500">{field.helper}</p>}
			</div>
		);
	}

	if (field.type === "image") {
		if (!allowImageVariables) {
			return (
				<ImagePickerInput
					label={field.label}
					value={imageValue}
					helper={field.helper}
					folder={field.folder}
					onChange={(next) => updateComponentValue(componentIndex, fullPath, next ?? "")}
					disabled={!editable}
				/>
			);
		}

		return (
			<div className="space-y-2">
				{imageVariableMode ? (
					<div className="space-y-1">
						<label className="text-xs font-semibold text-slate-600">{field.label}</label>
						<input
							type="text"
							value={isVariableValue ? imageSrc : ""}
							placeholder="{image}"
							onChange={(e) => {
								const nextValue = e.target.value;
								if (!nextValue.trim()) {
									updateComponentValue(componentIndex, fullPath, "");
									return;
								}
								if (imageOptions && typeof imageOptions === "object") {
									updateComponentValue(componentIndex, fullPath, { src: nextValue, options: imageOptions });
									return;
								}
								updateComponentValue(componentIndex, fullPath, nextValue);
							}}
							disabled={!editable}
							className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
						/>
						<div className="flex flex-wrap items-center justify-between gap-2">
							{field.helper ? <p className="text-[11px] text-slate-500">{field.helper}</p> : <span />}
							{editable && (
								<button
									type="button"
									onClick={() => {
										setImageVariableMode(false);
										updateComponentValue(componentIndex, fullPath, "");
									}}
									className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-slate-300 hover:bg-slate-50"
								>
									Use image picker
								</button>
							)}
						</div>
					</div>
				) : (
					<>
						<ImagePickerInput
							label={field.label}
							value={imageValue}
							helper={field.helper}
							folder={field.folder}
							onChange={(next) => updateComponentValue(componentIndex, fullPath, next ?? "")}
							disabled={!editable}
						/>
						{editable && (
							<div>
								<button
									type="button"
									onClick={() => setImageVariableMode(true)}
									className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-slate-300 hover:bg-slate-50"
								>
									Use variable
								</button>
							</div>
						)}
					</>
				)}
			</div>
		);
	}

	if (field.type === "select") {
		return (
			<div className="space-y-1">
				<label className="text-xs font-semibold text-slate-600">{field.label}</label>
				<select
					value={(value ?? "") as string}
					onChange={(e) => updateComponentValue(componentIndex, fullPath, e.target.value)}
					disabled={!editable}
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
				>
					{field.options.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
				{field.helper && <p className="text-[11px] text-slate-500">{field.helper}</p>}
			</div>
		);
	}

	if (field.type === "dynamicPageSelect") {
		return (
			<DynamicPageSelectControl
				editable={editable}
				field={field as DynamicPageSelectFieldConfig}
				componentIndex={componentIndex}
				fullPath={fullPath}
				previewComponents={previewComponents}
				updateComponentValue={updateComponentValue}
				value={value}
			/>
		);
	}

	if (field.type === "categoryPath") {
		const pathValue = Array.isArray(value) ? (value as string[]) : [];
		return (
			<div className="space-y-1">
				<CategoryPathSelect
					categories={categories ?? []}
					value={pathValue}
					onChange={(next) => updateComponentValue(componentIndex, fullPath, next)}
					label={field.label}
					helper={field.helper}
					disabled={!editable}
				/>
			</div>
		);
	}

	if (field.type === "href") {
		const hrefValue =
			typeof value === "string" ? ({ slug: value } as HrefTarget) : (value as HrefTarget | null | undefined) ?? null;
		return (
			<div className="space-y-1">
				<label className="text-xs font-semibold text-slate-600">{field.label}</label>
				<HrefInput
					value={hrefValue}
					onChange={(next) => updateComponentValue(componentIndex, fullPath, next ?? { slug: "" })}
					allowExternal={field.allowExternal ?? true}
					allowManual={field.allowManual ?? true}
					allowPage={field.allowPage ?? true}
					disabled={!editable}
				/>
				{field.helper && <p className="text-[11px] text-slate-500">{field.helper}</p>}
			</div>
		);
	}

	if (field.type === "file") {
		const fileValue = (value as HrefTarget | null | undefined) ?? null;
		const fileField = field as import("../../Pages/pageComponentLibrary").FileFieldConfig;
		return (
			<div className="space-y-1">
				<FilePickerInput
					label={fileField.label}
					helper={fileField.helper}
					value={fileValue}
					onChange={(next) => updateComponentValue(componentIndex, fullPath, next ?? { slug: "" })}
					disabled={!editable}
					folder={fileField.folder ?? "documents"}
				/>
			</div>
		);
	}

	if (field.type === "contentTypeSelect") {
		const selected = (value ?? "") as string;
		return (
			<div className="space-y-1">
				<label className="text-xs font-semibold text-slate-600">{field.label}</label>
				<select
					value={selected}
					onChange={(e) => updateComponentValue(componentIndex, fullPath, e.target.value)}
					disabled={!editable}
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
				>
					<option value="">Select a content type</option>
					{contentTypeConfigs.map((config) => (
						<option key={config.id ?? config.title} value={config.id ?? ""}>
							{config.title ?? config.id}
						</option>
					))}
				</select>
				{field.helper && <p className="text-[11px] text-slate-500">{field.helper}</p>}
			</div>
		);
	}

	if (field.type === "contentFieldSelect") {
		return (
			<ContentFieldSelectControl
				editable={editable}
				field={field as ContentFieldSelectFieldConfig}
				componentIndex={componentIndex}
				fullPath={fullPath}
				parentPath={parentPath}
				previewComponents={previewComponents}
				updateComponentValue={updateComponentValue}
				value={value}
			/>
		);
	}

	if (field.type === "contentFieldValue") {
		return (
			<ContentFieldValueControl
				editable={editable}
				field={field as ContentFieldValueFieldConfig}
				componentIndex={componentIndex}
				fullPath={fullPath}
				parentPath={parentPath}
				previewComponents={previewComponents}
				updateComponentValue={updateComponentValue}
				value={value}
			/>
		);
	}

	if (field.type === "object") {
		return (
			<PageEditorObjectField
				editable={editable}
				field={field}
				componentIndex={componentIndex}
				parentPath={fullPath}
				previewComponents={previewComponents}
				updateComponentValue={updateComponentValue}
				allowImageVariables={allowImageVariables}
			/>
		);
	}

	if (field.type === "array") {
		const arrField = field as ArrayFieldConfig;
		if (arrField.itemEditor === "richParagraph") {
			return (
				<ParagraphArrayField
					editable={editable}
					field={arrField}
					componentIndex={componentIndex}
					parentPath={fullPath}
					previewComponents={previewComponents}
					updateComponentValue={updateComponentValue}
					value={value}
				/>
			);
		}
		return (
			<PageEditorArrayField
				editable={editable}
				field={arrField}
				componentIndex={componentIndex}
				parentPath={fullPath}
				previewComponents={previewComponents}
				updateComponentValue={updateComponentValue}
				value={value}
				allowImageVariables={allowImageVariables}
			/>
		);
	}

	return null;
};

export default PageEditorFieldControl;
